import { createTrainingSessionId } from "./trainingLiveState"
import { buildLinkedWatchExercises } from "./watchSyncService"

const copy = (value) => JSON.parse(JSON.stringify(value))
const fields = [["Gewicht", "weightKg", "targetWeightKg", "kg"], ["Wiederholungen", "reps", "targetReps", ""],
  ["Dauer", "durationSeconds", "targetDurationSeconds", "s"], ["Distanz", "distanceMeters", "targetDistanceMeters", "m"]]
const terminal = (phase) => ["ending", "completed", "discarded"].includes(phase)
export const isSharedWatchWorkout = (state) => state.liveSession?.owner === "watch" && Boolean(state.linkedWatchWorkout)

function settings(value, target, metadata) {
  const result = fields.filter(([, actual, planned]) => value[target ? planned : actual] != null)
    .map(([text, actual, planned, unit]) => ({ text, value: value[target ? planned : actual], unit }))
  if (target && value.targetRestSeconds != null) result.push({ text: "Pause", value: value.targetRestSeconds, unit: "s" })
  // Metadata on a setting survives Vuex's JSON persistence, unlike array properties.
  if (!result.length) result.push({ text: "Wiederholungen", value: 0, unit: "" })
  result[0]._watch = metadata
  return result
}

function picture(url) {
  try { const parsed = new URL(url); return parsed.hostname === "app.wellcomefit.ch" && parsed.pathname.startsWith("/img/uebungen/") ? decodeURIComponent(parsed.pathname.slice(14)) : null } catch (_) { return null }
}

export function sharedWatchProjection(snapshot, previous = {}) {
  const workout = snapshot.workout
  if (!workout?.token || !Array.isArray(workout.rows) || !Number.isFinite(workout.startedAt) ||
    !["training_plan", "free_workout"].includes(workout.activityType)) return null
  const exercises = [], completedSetsMap = {}, locations = new Map(), groups = new Map()
  for (const row of workout.rows) {
    const ex = row.exercise
    if (!ex?.instanceId || !Array.isArray(ex.sets) || !Array.isArray(row.sets)) return null
    const key = ex.parentExerciseId || ex.instanceId
    let group = groups.get(key)
    if (!group) {
      group = ex.parentExerciseId ? { _id: ex.parentExerciseId, supersetId: ex.parentExerciseId, title: "Supersatz", isSuperset: true, exercises: [] } : null
      const item = group || { _id: ex.id, title: ex.title, picture: picture(ex.imageUrl), sets: [], warmup: null, isSuperset: false }
      item._watchGroupId = key
      exercises.push(item); groups.set(key, item); group = item
    }
    const index = exercises.indexOf(group)
    let exercise = group
    if (group.isSuperset) {
      exercise = group.exercises.find((child) => child._id === ex.id)
      if (!exercise) { exercise = { _id: ex.id, title: ex.title, picture: picture(ex.imageUrl), sets: [], warmup: null }; group.exercises.push(exercise) }
    }
    const subIndex = group.isSuperset ? group.exercises.indexOf(exercise) : 0
    locations.set(ex.instanceId, { index, subIndex })
    for (const target of ex.sets) {
      const values = settings(target, true, { instanceId: ex.instanceId, targetId: target.id })
      if (target.isWarmup) exercise.warmup = values
      else exercise.sets.push(values)
    }
    exercise.set_exercise_settings = (exercise.sets[0] || []).map(({ _watch, ...value }) => value)
    const done = completedSetsMap[index] || (completedSetsMap[index] = [])
    for (const set of row.sets) {
      const values = settings(set, false, { instanceId: ex.instanceId, result: copy(set) })
      if (group.isSuperset) values.subExerciseId = ex.id
      if (set.isWarmup) values.isWarmup = true
      done.push(values)
    }
  }
  const location = locations.get(snapshot.exerciseInstanceId)
  const selectedId = previous.activeSplit?.exercises?.[previous.selectedExerciseIndex]?._watchGroupId
  const selectedIndex = exercises.findIndex((ex) => ex._watchGroupId === selectedId)
  const restStartedAt = snapshot.restStartedAt || Date.now()
  return {
    activeSplit: { title: snapshot.title, _id: workout.splitKey, exercises, isFreeTraining: workout.activityType === "free_workout" },
    activePlanID: workout.workoutId || null, completedSetsMap,
    workoutStartTime: new Date(workout.startedAt).toISOString(),
    selectedExerciseIndex: selectedIndex >= 0 ? selectedIndex : null,
    activeSupersetSubIndex: selectedIndex >= 0 ? previous.activeSupersetSubIndex || 0 : 0,
    liveExerciseContext: location || null,
    liveSession: { clientSessionId: snapshot.clientSessionId, userId: snapshot.userId, owner: "watch", startedAt: workout.startedAt,
      phase: snapshot.phase, revision: snapshot.revision, elapsedSeconds: snapshot.elapsedSeconds, elapsedAnchorAt: snapshot.elapsedAnchorAt,
      syncState: snapshot.syncState, savedAt: snapshot.syncState === "saved" ? Date.now() : null, lastActivityAt: snapshot.updatedAt },
    watchCompanion: { ...previous.watchCompanion, clientSessionId: snapshot.clientSessionId, platform: snapshot.platform,
      status: terminal(snapshot.phase) ? "ended" : snapshot.phase === "paused" ? "paused" : "running",
      recordedAt: new Date(snapshot.updatedAt).toISOString(), elapsedSeconds: snapshot.elapsedSeconds,
      currentHeartRate: snapshot.heartRate || null, activeEnergyKilocalories: snapshot.energy || 0, error: null },
    timerState: { isActive: snapshot.phase === "resting" && snapshot.restEndsAt > Date.now(), startTime: restStartedAt,
      duration: Math.max(0, (snapshot.restEndsAt - restStartedAt) / 1000) || 0,
      originalDuration: Math.max(0, (snapshot.restEndsAt - restStartedAt) / 1000) || 0,
      isMinimized: true, exerciseIndex: location?.index ?? null, subExerciseId: null },
  }
}

function readValues(raw, target) {
  const result = {}
  for (const [label, actual, planned] of fields) {
    const setting = raw.find((item) => item.text === label)
    let value = setting?.value
    if (value === "" || value == null) { result[target ? planned : actual] = null; continue }
    value = Number(value)
    if (label === "Dauer" && (setting.unit || (target ? "min" : "s")).includes("min")) value *= 60
    if (label === "Distanz" && setting.unit === "km") value *= 1000
    const maximum = label === "Gewicht" ? 2000 : label === "Wiederholungen" ? 10000 : label === "Dauer" ? 86400 : 10000000
    if (!Number.isFinite(value) || value < 0 || value > maximum) throw new Error("Bitte prüfe die eingegebenen Satzwerte.")
    result[target ? planned : actual] = ["Wiederholungen", "Dauer"].includes(label) ? Math.round(value) : value
  }
  if (target) result.targetRestSeconds = Math.min(900, Math.max(0, Number(raw.find((item) => item.text === "Pause")?.value) || 0)) || null
  return result
}

// Preserve native exercise/set identities while using the ordinary phone editor.
export function sharedWatchRows(state) {
  const source = state.linkedWatchWorkout.snapshot.workout.rows
  const originals = new Map(source.map((row) => [row.exercise.instanceId, row]))
  const rows = []
  ;(state.activeSplit?.exercises || []).forEach((group, groupIndex) => {
    const groupStart = rows.length
    if (!group._watchGroupId) group._watchGroupId = createTrainingSessionId()
    const generated = buildLinkedWatchExercises(group, group._watchGroupId)
    const children = group.isSuperset ? group.exercises : [group]
    children.forEach((exercise, subIndex) => {
      const rawCompleted = (state.completedSetsMap[groupIndex] || []).filter((set) => !group.isSuperset ||
        (set.subExerciseId || originals.get(set[0]?._watch?.instanceId)?.exercise.id) === exercise._id)
      const completed = rawCompleted
        .filter((set) => Array.isArray(set) && !set.skipped && !set.some((item) => item.skipped))
      const skippedWarmup = rawCompleted.some((set) => set.skipped && (set.type === "warmup" || set.isWarmup))
      const warmup = skippedWarmup ? null : exercise.warmup?.length ? exercise.warmup : completed.find((set) => set.isWarmup || set[0]?._watch?.result?.isWarmup)
      const targets = [...(warmup ? [warmup] : []), ...(exercise.sets?.length ? exercise.sets : [[]])]
      const childRows = new Map()
      targets.forEach((raw, index) => {
        const values = Array.isArray(raw) ? raw : raw.settings || []
        const meta = values[0]?._watch
        const fallback = generated.find((ex) => ex.id === String(exercise._id) && (!group.isSuperset || ex.subtitle === `Supersatz · Runde ${index + 1}`)) || generated[subIndex] || generated[0]
        const firstTarget = exercise.sets?.[0]?.[0]?._watch
        const old = originals.get(meta?.instanceId || (!group.isSuperset ? firstTarget?.instanceId : null))
        const native = old?.exercise.id === String(exercise._id) ? old.exercise : fallback
        if (!native) throw new Error("Die Übung kann noch nicht mit der Uhr abgeglichen werden.")
        let row = childRows.get(native.instanceId)
        if (!row) { row = { exercise: { ...copy(native), title: exercise.title, statistics: null, sets: [] }, sets: [] }; childRows.set(native.instanceId, row); rows.push(row) }
        const originalTarget = old?.exercise.sets.find((set) => set.id === meta?.targetId)
        const isWarmup = Boolean(warmup && index === 0)
        row.exercise.sets.push({ ...originalTarget, id: originalTarget?.id || `${native.instanceId}:${isWarmup ? 'warmup' : 'set:' + index}`, position: row.exercise.sets.length + 1,
          ...(isWarmup ? { isWarmup: true } : {}), ...readValues(values, true) })
      })
      completed.forEach((set, index) => {
        const meta = set[0]?._watch
        const candidates = [...childRows.values()]
        const row = childRows.get(meta?.instanceId) || candidates.find((item) => item.sets.length < item.exercise.sets.length) || candidates[candidates.length - 1]
        if (!row) return
        const old = meta?.result || originals.get(row.exercise.instanceId)?.sets[index]
        let target = row.exercise.sets[row.sets.length]
        if (!target) {
          target = { id: `${row.exercise.instanceId}:set:${row.exercise.sets.length}`, position: row.exercise.sets.length + 1, ...readValues(set, true) }
          row.exercise.sets.push(target)
        }
        const result = { ...old, id: old?.id || createTrainingSessionId(), targetSetId: target.id, position: target.position,
          completedAt: old?.completedAt || new Date().toISOString().replace(/\.\d{3}Z$/, "Z"),
          ...(target.isWarmup ? { isWarmup: true } : {}), ...readValues(set, false) }
        row.sets.push(result)
        if (set[0]) set[0]._watch = { instanceId: row.exercise.instanceId, result: copy(result) }
      })
    })
    if (group.isSuperset) {
      const sourceOrder = source.map((row) => row.exercise.instanceId)
      const generatedOrder = generated.map((exercise) => exercise.instanceId)
      const order = (row) => { const index = sourceOrder.indexOf(row.exercise.instanceId); return index >= 0 ? index : source.length + generatedOrder.indexOf(row.exercise.instanceId) }
      rows.splice(groupStart, rows.length - groupStart, ...rows.slice(groupStart).sort((a, b) => order(a) - order(b)))
    }
  })
  if (rows.length > 150 || rows.some((row) => row.exercise.sets.length > 30)) throw new Error("Dieses Training überschreitet die Satzanzahl der Uhr.")
  return rows
}

const canonical = (value) => value && typeof value === "object" ? Array.isArray(value) ? value.map(canonical) :
  Object.fromEntries(Object.keys(value).sort().filter((key) => value[key] != null && key !== "statistics").map((key) => [key, canonical(value[key])])) : value
export const sameWatchRows = (left, right) => JSON.stringify(canonical(left)) === JSON.stringify(canonical(right))
export const watchSessionEnded = (state) => terminal(state?.phase)

// Rebase independent edits after a concurrent Watch input. A result is keyed by
// its target slot, so recording the same set twice can never create two entries.
export function mergeWatchRows(base, local, remote, preferLocal = false) {
  const equal = sameWatchRows
  const key = (value) => value?.exercise?.instanceId || value?.targetSetId || value?.id
  function merge(before, mine, theirs) {
    if (equal(mine, before)) return theirs
    if (equal(theirs, before) || equal(mine, theirs)) return mine
    if (Array.isArray(mine) && Array.isArray(theirs) && Array.isArray(before)) {
      if (![...before, ...mine, ...theirs].every(key)) throw new Error("conflict")
      const b = new Map(before.map((item) => [key(item), item])), m = new Map(mine.map((item) => [key(item), item])), t = new Map(theirs.map((item) => [key(item), item]))
      const orderChanged = !equal(before.map(key).filter((id) => m.has(id)), mine.map(key).filter((id) => b.has(id)))
      const order = orderChanged ? [...m.keys(), ...t.keys()] : [...t.keys(), ...m.keys()]
      return [...new Set(order)].map((id) => merge(b.get(id), m.get(id), t.get(id))).filter(Boolean)
    }
    if (before && mine && theirs && typeof mine === "object" && typeof theirs === "object" && !Array.isArray(mine)) {
      return Object.fromEntries([...new Set([...Object.keys(before), ...Object.keys(mine), ...Object.keys(theirs)])]
        .map((name) => [name, merge(before[name], mine[name], theirs[name])]).filter(([, value]) => value !== undefined))
    }
    if (preferLocal) return mine
    throw new Error("conflict")
  }
  try { return { rows: merge(base, local, remote), conflict: false } } catch (_) { return { rows: local, conflict: true } }
}
